import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppState } from "../../app-state";
import { readRecoveryPackages, type RecoveryRecord } from "../../lib/recovery";
import { fetchInscriptionTransactionStatuses } from "../../lib/api";
import { transactionExplorerUrl } from "../../lib/explorer-links";
import type { SignedPackage } from "../../lib/types";
import { RecoveryRefund } from "../inscribe/RecoveryRefund";
import { resumeRecoveryPackage } from "../../lib/recovery-api";
export function RecoveryPanel({
  onSelect,
}: {
  onSelect?: (value: SignedPackage) => void;
}) {
  const { wallet } = useAppState();
  const [records, setRecords] = useState<RecoveryRecord[]>([]);
  const [states, setStates] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const resume = async (record: RecoveryRecord) => {
    setBusy(true); setError("");
    try { await resumeRecoveryPackage(record.package); setRefresh(n => n + 1); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Inscription resume failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    let active = true;
    setRecords([]);
    setStates({});
    setError("");
    if (wallet)
      void readRecoveryPackages(wallet.payment.address)
        .then(async (rows) => {
          if (!active) return;
          setRecords(rows);
          if (!rows.length) return;
          const statuses = await fetchInscriptionTransactionStatuses(
            rows.flatMap((r) => [r.package.commitTxid, r.package.revealTxid]),
          );
          if (active)
            setStates(
              Object.fromEntries(
                rows.map((r) => {
                  const reveal = statuses[r.package.revealTxid];
                  const commit = statuses[r.package.commitTxid];
                  return [
                    r.id,
                    reveal?.confirmed
                      ? "CONFIRMED"
                      : reveal
                        ? "REVEAL PENDING"
                        : commit
                          ? "PAYMENT SEEN · CHECK REVEAL"
                          : "STATUS UNKNOWN · CHECK EXPLORER",
                  ];
                }),
              ),
            );
        })
        .catch((e) => {
          if (active)
            setError(
              e instanceof Error ? e.message : "Recovery check unavailable",
            );
        });
    return () => {
      active = false;
    };
  }, [wallet, refresh]);
  return (
    <details className="experience-help recovery-panel">
      <summary>TRANSACTION RECOVERY <span>{records.length ? `${records.length} saved` : "Saved packages"}</span></summary>
      <p>
        Signed inscription packages are saved in this browser before broadcast.
        Their IDs are calculated locally and do not prove they were sent.
        A network timeout may happen after a transaction was accepted. Check
        both transactions before retrying; never create a replacement
        inscription just because a request timed out.
      </p>
      <p>
        Resume Inscription submits the saved reveal after Worker validation and skips
        a payment already known to Bitcoin. Review Refund returns an eligible
        commit to your connected wallet. Nothing signs or sends automatically.
      </p>
      <button type="button" onClick={() => setRefresh((n) => n + 1)}>
        CHECK STATUS
      </button>
      {error ? <p role="alert">{error}</p> : null}
      {records.map((r) => (
        <article key={`${wallet?.provider}:${wallet?.payment.address}:${wallet?.ordinals.address}:${r.id}`}>
          <strong>{states[r.id] ?? "CHECKING…"}</strong>
          <p>{new Date(r.savedAt).toLocaleString()}</p>
          <a
            href={transactionExplorerUrl(r.package.commitTxid)}
            target="_blank"
            rel="noreferrer"
          >
            PAYMENT ↗
          </a>
          {" · "}
          <a
            href={transactionExplorerUrl(r.package.revealTxid)}
            target="_blank"
            rel="noreferrer"
          >
            INSCRIPTION ↗
          </a>
          <button type="button" disabled={busy || Boolean(r.refundAttempt)} onClick={() => void resume(r)}>RESUME INSCRIPTION</button>
          <RecoveryRefund record={r} onSigned={attempt => setRecords(current => current.map(row => row.id === r.id ? { ...row, refundAttempt: attempt } : row))} />
          {onSelect ? (
            <button type="button" onClick={() => onSelect(r.package)}>
              LOAD SAVED PACKAGE
            </button>
          ) : (
            <Link to="/console">RESUME IN BROADCAST</Link>
          )}
        </article>
      ))}
      {!records.length ? (
        <p>
          No saved packages for this wallet in this browser. Older downloaded
          packages can be opened in <Link to="/console">Broadcast</Link>.
        </p>
      ) : null}
    </details>
  );
}
